import { ActivityFeedEntityLinkDto, EntityTypeEnum } from 'api/activity-feed';

// ============================================
// TYPES
// ============================================

export interface ParsedMessagePart {
  type: 'text' | 'entity';
  content: string;
  entityType?: EntityTypeEnum;
  entityId?: string;
}

// ============================================
// ENTITY NAVIGATION
// ============================================

/**
 * Generates the navigation URL for an entity based on its type
 */
export const getEntityUrl = (
  entityType: EntityTypeEnum,
  entityId: string,
  riskId?: string,
  entities?: ActivityFeedEntityLinkDto[]
): string | null => {
  switch (entityType) {
    case EntityTypeEnum.Contrat:
      return `/mes-contrats/details/${entityId}`;

    case EntityTypeEnum.Devis:
      // For Devis, we need the riskId (first param) and devisId (second param)
      if (riskId) {
        return `/simulation/contractualisation/${riskId}/${entityId}`;
      }
      // Try to find RisqueAssure in entities
      const risqueAssure = entities?.find(
        (e) => e.entityType === EntityTypeEnum.RisqueAssure
      );
      if (risqueAssure) {
        return `/simulation/contractualisation/${risqueAssure.entityId}/${entityId}`;
      }
      return null;

    case EntityTypeEnum.Personne:
      return `/mes-clients/${entityId}`;

    case EntityTypeEnum.Note:
      return `/mes-notes?noteId=${entityId}`;

    case EntityTypeEnum.RisqueAssure:
      // RisqueAssure doesn't have a direct page, could link to simulation
      return null;

    case EntityTypeEnum.BienImmobilier:
      // TODO: Add route when available
      return null;

    case EntityTypeEnum.Locataire:
      // TODO: Add route when available
      return null;

    case EntityTypeEnum.Mandat:
      // TODO: Add route when available
      return null;

    case EntityTypeEnum.Quittance:
      // TODO: Add route when available
      return null;

    case EntityTypeEnum.Document:
      // TODO: Add route when available
      return null;

    case EntityTypeEnum.Courtier:
      // TODO: Add route when available
      return null;

    case EntityTypeEnum.Garantie:
      // TODO: Add route when available
      return null;

    default:
      return null;
  }
};

// ============================================
// MESSAGE PARSER
// ============================================

/**
 * Parses a message containing {EntityType:Guid} references
 * and returns an array of text and entity parts
 */
export const parseMessage = (
  message: string,
  entities: ActivityFeedEntityLinkDto[]
): ParsedMessagePart[] => {
  const parts: ParsedMessagePart[] = [];
  // Regex to match {EntityType:Guid} pattern
  const entityPattern = /\{([^:}]+):([^}]+)\}/g;
  let lastIndex = 0;
  let match;

  while ((match = entityPattern.exec(message)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: message.slice(lastIndex, match.index)
      });
    }

    const entityType = match[1] as EntityTypeEnum;
    const entityId = match[2];

    // Find the entity in the entities array to get display info
    const entity = entities.find(
      (e) => e.entityType === entityType && e.entityId === entityId
    );

    if (entity) {
      parts.push({
        type: 'entity',
        content: match[0], // The full match like {Contrat:xxx}
        entityType,
        entityId
      });
    } else {
      // If entity not found, treat as text
      parts.push({
        type: 'text',
        content: match[0]
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last match
  if (lastIndex < message.length) {
    parts.push({
      type: 'text',
      content: message.slice(lastIndex)
    });
  }

  return parts;
};

/**
 * Gets the display label for an entity from the entities array
 */
export const getEntityLabel = (
  entities: ActivityFeedEntityLinkDto[],
  entityType: EntityTypeEnum,
  entityId: string
): string => {
  const entity = entities.find(
    (e) => e.entityType === entityType && e.entityId === entityId
  );

  if (entity?.entityLabel) {
    return entity.entityLabel;
  }

  return 'NaN';
};
